import React, { useState, useEffect } from "react";
import { 
  getAllRoles, 
  createRole, 
  updateRole, 
  deleteRole 
} from "../../../api/apiFunction/roleServices";
import { 
  getAllAdminUsers, 
  createAdminUser, 
  assignAdminRole,
  deleteAdminUser,
  updateUser
} from "../../../api/apiFunction/userServices";
import { toast } from "react-toastify";
import { 
  Shield, 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  Users,
  UserPlus
} from "lucide-react";

// Define all available admin dashboard routes
const ALL_ADMIN_ROUTES = [
  { path: "/admin", label: "Dashboard", icon: "📊" },
  { path: "/admin/organizations", label: "Organizations", icon: "🏢" },
  { path: "/admin/requests", label: "Individuals", icon: "👤" },
  { path: "/admin/personas", label: "Personas", icon: "👥" },
  { path: "/admin/newsletter", label: "Newsletter & Email", icon: "📧" },
  { path: "/admin/usage", label: "Usage", icon: "📈" },
  { path: "/admin/usage/individuals", label: "Usage - Individuals", icon: "📊" },
  { path: "/admin/usage/organizations", label: "Usage - Organizations", icon: "📊" },
  { path: "/admin/plans", label: "Plan Settings", icon: "⚙️" },
  { path: "/admin/models", label: "Models", icon: "🤖" },
  { path: "/admin/partners", label: "Partners", icon: "🤝" },
  { path: "/admin/sentry-dashboard", label: "App Monitoring", icon: "⚠️" },
];

// Skeleton loader component
const LoadingSkeleton = () => (
  <div>
    {/* Header Skeleton */}
    <div className="flex items-center justify-between mb-6">
      <div>
        <div className="h-8 w-64 bg-bg-40 rounded animate-pulse mb-2" />
        <div className="h-4 w-96 bg-bg-40 rounded animate-pulse" />
      </div>
      <div className="h-10 w-32 bg-bg-40 rounded animate-pulse" />
    </div>

    {/* Tabs Skeleton */}
    <div className="flex gap-2 mb-6 border-b border-bd-50">
      <div className="h-10 w-32 bg-bg-40 rounded-t animate-pulse" />
      <div className="h-10 w-32 bg-bg-40 rounded-t animate-pulse" />
    </div>

    {/* Search Skeleton */}
    <div className="mb-6">
      <div className="h-10 w-full max-w-md bg-bg-40 rounded animate-pulse" />
    </div>

    {/* Cards Skeleton */}
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="border border-bd-50 rounded-lg bg-bg-60 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              <div className="h-6 w-48 bg-bg-40 rounded animate-pulse mb-2" />
              <div className="h-4 w-96 bg-bg-40 rounded animate-pulse" />
            </div>
            <div className="flex gap-2">
              <div className="h-9 w-20 bg-bg-40 rounded animate-pulse" />
              <div className="h-9 w-24 bg-bg-40 rounded animate-pulse" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-4 w-32 bg-bg-40 rounded animate-pulse mb-2" />
            <div className="flex flex-wrap gap-2">
              {[...Array(5)].map((_, j) => (
                <div key={j} className="h-6 w-24 bg-bg-40 rounded animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const RoleManagement = () => {
  const [activeTab, setActiveTab] = useState("roles"); // "roles" or "users"
  
  // Role states
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    allowedRoutes: []
  });
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [createRoleForm, setCreateRoleForm] = useState({
    name: "",
    description: "",
    allowedRoutes: []
  });
  const [roleSearchQuery, setRoleSearchQuery] = useState("");

  // User states
  const [adminUsers, setAdminUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    name: "",
    email: "",
    password: "",
    adminRoleId: ""
  });
  const [editUserForm, setEditUserForm] = useState({
    name: "",
    adminRoleId: ""
  });
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [showDeleteRoleModal, setShowDeleteRoleModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, usersRes] = await Promise.all([
        getAllRoles(),
        getAllAdminUsers()
      ]);
      
      if (rolesRes?.data) {
        setRoles(rolesRes.data);
      }
      
      if (usersRes?.data) {
        setAdminUsers(usersRes.data);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!createRoleForm.name.trim()) {
      toast.error("Role name is required");
      return;
    }

    try {
      const res = await createRole(createRoleForm);
      if (res?.data) {
        toast.success("Role created successfully");
        setShowCreateRoleModal(false);
        setCreateRoleForm({ name: "", description: "", allowedRoutes: [] });
        fetchData();
      } else {
        toast.error(res?.response?.data?.error || "Failed to create role");
      }
    } catch (error) {
      toast.error("Failed to create role");
    }
  };

  const handleCreateUser = async () => {
    if (!createUserForm.name || !createUserForm.email || !createUserForm.password) {
      toast.error("Name, email, and password are required");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(createUserForm.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Password validation
    if (createUserForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      const payload = {
        name: createUserForm.name,
        email: createUserForm.email,
        password: createUserForm.password,
        adminRoleId: createUserForm.adminRoleId || null
      };

      const res = await createAdminUser(payload);
      if (res?.data) {
        toast.success("Admin user created successfully");
        setShowCreateUserModal(false);
        setCreateUserForm({ name: "", email: "", password: "", adminRoleId: "" });
        fetchData();
      } else {
        toast.error(res?.response?.data?.error || "Failed to create admin user");
      }
    } catch (error) {
      toast.error("Failed to create admin user");
    }
  };

  const handleEditUserClick = (user) => {
    setEditingUser(user._id);
    setEditUserForm({
      name: user.name || "",
      adminRoleId: user.adminRole?._id || ""
    });
  };

  const handleCancelUserEdit = () => {
    setEditingUser(null);
    setEditUserForm({ name: "", adminRoleId: "" });
  };

  const handleSaveUserEdit = async (userId) => {
    if (!editUserForm.name.trim()) {
      toast.error("Name is required");
      return;
    }

    try {
      // Update user name
      const updateRes = await updateUser(userId, { name: editUserForm.name });
      if (!updateRes?.data && updateRes?.status !== 200) {
        toast.error(updateRes?.response?.data?.error || "Failed to update user name");
        return;
      }

      // Update role assignment
      const roleRes = await assignAdminRole(userId, editUserForm.adminRoleId || null);
      if (roleRes?.data || roleRes?.status === 200) {
        toast.success("User updated successfully");
        setEditingUser(null);
        setEditUserForm({ name: "", adminRoleId: "" });
        fetchData();
      } else {
        toast.error(roleRes?.response?.data?.error || "Failed to assign role");
      }
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = adminUsers.find(u => u._id === userId);
    if (user) {
      setUserToDelete(user);
      setShowDeleteUserModal(true);
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const res = await deleteAdminUser(userToDelete._id);
      if (res?.status === 200 || res?.data) {
        toast.success("User deleted successfully");
        setShowDeleteUserModal(false);
        setUserToDelete(null);
        fetchData();
      } else {
        toast.error(res?.response?.data?.error || "Failed to delete user");
      }
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleEditClick = (role) => {
    setEditingRole(role._id);
    setEditForm({
      name: role.name,
      description: role.description || "",
      allowedRoutes: role.allowedRoutes || []
    });
  };

  const handleCancelEdit = () => {
    setEditingRole(null);
    setEditForm({ name: "", description: "", allowedRoutes: [] });
  };

  const handleSaveEdit = async (roleId) => {
    if (!editForm.name.trim()) {
      toast.error("Role name is required");
      return;
    }

    try {
      const res = await updateRole(roleId, editForm);
      if (res?.data || res?.status === 200) {
        toast.success("Role updated successfully");
        setEditingRole(null);
        setEditForm({ name: "", description: "", allowedRoutes: [] });
        fetchData(); // Fixed: was calling fetchRoles() which doesn't exist
      } else {
        const errorMessage = res?.response?.data?.error || res?.data?.error || "Failed to update role";
        toast.error(errorMessage);
        console.error("Update role error:", res);
      }
    } catch (error) {
      console.error("Update role exception:", error);
      toast.error("Failed to update role");
    }
  };

  const handleDeleteRole = async (roleId) => {
    const role = roles.find(r => r._id === roleId);
    if (role) {
      setRoleToDelete(role);
      setShowDeleteRoleModal(true);
    }
  };

  const confirmDeleteRole = async () => {
    if (!roleToDelete) return;

    try {
      const res = await deleteRole(roleToDelete._id);
      if (res?.status === 200 || res?.data) {
        toast.success("Role deleted successfully");
        setShowDeleteRoleModal(false);
        setRoleToDelete(null);
        fetchData();
      } else {
        toast.error(res?.response?.data?.error || "Failed to delete role");
      }
    } catch (error) {
      toast.error("Failed to delete role");
    }
  };

  const toggleRoute = (routePath, isCreate = false) => {
    if (isCreate) {
      setCreateRoleForm(prev => ({
        ...prev,
        allowedRoutes: prev.allowedRoutes.includes(routePath)
          ? prev.allowedRoutes.filter(r => r !== routePath)
          : [...prev.allowedRoutes, routePath]
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        allowedRoutes: prev.allowedRoutes.includes(routePath)
          ? prev.allowedRoutes.filter(r => r !== routePath)
          : [...prev.allowedRoutes, routePath]
      }));
    }
  };

  const toggleSelectAll = (isCreate = false) => {
    if (isCreate) {
      setCreateRoleForm(prev => ({
        ...prev,
        allowedRoutes: prev.allowedRoutes.length === ALL_ADMIN_ROUTES.length
          ? []
          : ALL_ADMIN_ROUTES.map(route => route.path)
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        allowedRoutes: prev.allowedRoutes.length === ALL_ADMIN_ROUTES.length
          ? []
          : ALL_ADMIN_ROUTES.map(route => route.path)
      }));
    }
  };

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(roleSearchQuery.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(roleSearchQuery.toLowerCase()))
  );

  const filteredUsers = adminUsers.filter(user =>
    user.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    (user.adminRole?.name && user.adminRole.name.toLowerCase().includes(userSearchQuery.toLowerCase()))
  );

  return (
    <div className="h-full p-6 overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-fg-50 flex items-center gap-2">
                  <Shield className="w-6 h-6" />
                  Role & User Management
                </h1>
                <p className="text-sm text-fg-60 mt-1">
                  Manage admin roles, route permissions, and create users with role assignments
                </p>
              </div>
              {activeTab === "roles" ? (
                <button
                  onClick={() => setShowCreateRoleModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-ac-01 text-white rounded-lg hover:bg-ac-02 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Role
                </button>
              ) : (
                <button
                  onClick={() => setShowCreateUserModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-ac-01 text-white rounded-lg hover:bg-ac-02 transition-colors"
                >
                  <UserPlus className="w-4 h-4" />
                  Create User
                </button>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-bd-50">
              <button
                onClick={() => setActiveTab("roles")}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === "roles"
                    ? "text-ac-01 border-b-2 border-ac-01"
                    : "text-fg-60 hover:text-fg-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Roles ({roles.length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab("users")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "users"
                ? "text-ac-01 border-b-2 border-ac-01"
                : "text-fg-60 hover:text-fg-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users ({adminUsers.length})
            </div>
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder={activeTab === "roles" ? "Search roles..." : "Search users by name, email, or role..."}
            value={activeTab === "roles" ? roleSearchQuery : userSearchQuery}
            onChange={(e) => activeTab === "roles" ? setRoleSearchQuery(e.target.value) : setUserSearchQuery(e.target.value)}
            className="w-full max-w-md px-4 py-2 border border-bd-50 rounded-lg bg-bg-60 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-01"
          />
        </div>

        {/* Roles Tab Content */}
        {activeTab === "roles" && (
          <>
            {filteredRoles.length === 0 ? (
              <div className="text-center py-12 text-fg-60">
                {roleSearchQuery ? "No roles found matching your search" : "No roles created yet"}
              </div>
            ) : (
          <div className="space-y-4">
            {filteredRoles.map((role) => (
              <div
                key={role._id}
                className="border border-bd-50 rounded-lg bg-bg-60 p-6"
              >
                {editingRole === role._id ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-fg-50">Edit Role</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(role._id)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-ac-01 text-white rounded-md hover:bg-ac-02 transition-colors"
                        >
                          <Save className="w-4 h-4" />
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="flex items-center gap-2 px-3 py-1.5 bg-bg-50 text-fg-60 border border-bd-50 rounded-md hover:bg-bg-40 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          Cancel
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-fg-60 mb-1">
                          Role Name *
                        </label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-bd-50 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-01"
                          placeholder="e.g., Finance, Marketing, Support"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-fg-60 mb-1">
                          Description
                        </label>
                        <textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-3 py-2 border border-bd-50 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-01"
                          placeholder="Role description..."
                          rows={2}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-fg-60 mb-2">
                          Allowed Routes
                        </label>
                        <div className="border border-bd-50 rounded-md p-3 bg-bg-50 max-h-64 overflow-y-auto">
                          <div className="space-y-2">
                            {/* Select All Checkbox */}
                            <label className="flex items-center gap-2 p-2 hover:bg-bg-60 rounded cursor-pointer border-b border-bd-50 mb-2">
                              <input
                                type="checkbox"
                                checked={editForm.allowedRoutes.length === ALL_ADMIN_ROUTES.length}
                                onChange={() => toggleSelectAll(false)}
                                className="w-4 h-4 text-ac-01 rounded focus:ring-ac-01"
                              />
                              <span className="text-sm font-semibold text-fg-50">
                                Select All Routes
                              </span>
                            </label>
                            {ALL_ADMIN_ROUTES.map((route) => {
                              const isSelected = editForm.allowedRoutes.includes(route.path);
                              return (
                                <label
                                  key={route.path}
                                  className="flex items-center gap-2 p-2 hover:bg-bg-60 rounded cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleRoute(route.path)}
                                    className="w-4 h-4 text-ac-01 rounded focus:ring-ac-01"
                                  />
                                  <span className="text-sm text-fg-50">
                                    {route.icon} {route.label}
                                  </span>
                                  <span className="text-xs text-fg-60 ml-auto">
                                    {route.path}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        <p className="text-xs text-fg-60 mt-1">
                          Selected {editForm.allowedRoutes.length} of {ALL_ADMIN_ROUTES.length} routes
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-fg-50 flex items-center gap-2">
                          <Shield className="w-5 h-5" />
                          {role.name}
                        </h3>
                        {role.description && (
                          <p className="text-sm text-fg-60 mt-1">{role.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditClick(role)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-bg-50 text-fg-60 border border-bd-50 rounded-md hover:bg-bg-40 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteRole(role._id)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 text-red-500 border border-red-500/20 rounded-md hover:bg-red-500/20 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-fg-60 mb-2">
                        Allowed Routes ({role.allowedRoutes?.length || 0}):
                      </p>
                      {role.allowedRoutes && role.allowedRoutes.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {role.allowedRoutes.map((routePath) => {
                            const route = ALL_ADMIN_ROUTES.find(r => r.path === routePath);
                            return (
                              <span
                                key={routePath}
                                className="px-2 py-1 bg-ac-01/20 text-ac-01 rounded-md text-xs flex items-center gap-1"
                              >
                                {route?.icon || "🔗"} {route?.label || routePath}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-fg-60">No routes assigned</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
            )}
          </>
        )}

        {/* Users Tab Content */}
        {activeTab === "users" && (
          <>
            {filteredUsers.length === 0 ? (
              <div className="text-center py-12 text-fg-60">
                {userSearchQuery ? "No users found matching your search" : "No admin users created yet"}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredUsers.map((user) => (
                  <div
                    key={user._id}
                    className="border border-bd-50 rounded-lg bg-bg-60 p-6"
                  >
                    {editingUser === user._id ? (
                      // Edit Mode
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-fg-50">Edit User</h3>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveUserEdit(user._id)}
                              className="flex items-center gap-2 px-3 py-1.5 bg-ac-01 text-white rounded-md hover:bg-ac-02 transition-colors"
                            >
                              <Save className="w-4 h-4" />
                              Save
                            </button>
                            <button
                              onClick={handleCancelUserEdit}
                              className="flex items-center gap-2 px-3 py-1.5 bg-bg-50 text-fg-60 border border-bd-50 rounded-md hover:bg-bg-40 transition-colors"
                            >
                              <X className="w-4 h-4" />
                              Cancel
                            </button>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-fg-60 mb-1">
                              Name *
                            </label>
                            <input
                              type="text"
                              value={editUserForm.name}
                              onChange={(e) => setEditUserForm(prev => ({ ...prev, name: e.target.value }))}
                              className="w-full max-w-md px-3 py-2 border border-bd-50 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-01"
                              placeholder="User name"
                            />
                          </div>

                        

                          <div>
                            <label className="block text-sm font-medium text-fg-60 mb-2">
                              Assign Role
                            </label>
                            <select
                              value={editUserForm.adminRoleId}
                              onChange={(e) => setEditUserForm(prev => ({ ...prev, adminRoleId: e.target.value }))}
                              className="w-full max-w-md px-3 py-2 border border-bd-50 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-01"
                            >
                              <option value="">No Role (Full Access)</option>
                              {roles.map((role) => (
                                <option key={role._id} value={role._id}>
                                  {role.name}
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-fg-60 mt-1">
                              Select a role to restrict access, or leave empty for full admin access
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-fg-50">{user.name}</h3>
                              <span className={`px-2 py-1 rounded-md text-xs font-semibold ${
                                user.role === "super admin" || user.role === "super_admin"
                                  ? "bg-purple-500/20 text-purple-400"
                                  : "bg-blue-500/20 text-blue-400"
                              }`}>
                                {user.role === "super admin" || user.role === "super_admin" 
                                  ? "Super Admin" 
                                  : "Admin"}
                              </span>
                              {user.status ? (
                                <span className="px-2 py-1 rounded-md text-xs font-semibold bg-green-500/20 text-green-400">
                                  Active
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded-md text-xs font-semibold bg-red-500/20 text-red-400">
                                  Inactive
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-fg-60">{user.email}</p>
                            {user.adminRole ? (
                              <div className="mt-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <Shield className="w-4 h-4 text-fg-60" />
                                  <span className="text-sm text-fg-50">
                                    Role: <span className="font-semibold">{user.adminRole.name}</span>
                                  </span>
                                  {user.adminRole.allowedRoutes && user.adminRole.allowedRoutes.length > 0 && (
                                    <span className="text-xs text-fg-60">
                                      ({user.adminRole.allowedRoutes.length} routes)
                                    </span>
                                  )}
                                </div>
                                {user.adminRole.allowedRoutes && user.adminRole.allowedRoutes.length > 0 ? (
                                  <div>
                                    <p className="text-xs font-medium text-fg-60 mb-2">Allowed Routes:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {user.adminRole.allowedRoutes.map((routePath) => {
                                        const route = ALL_ADMIN_ROUTES.find(r => r.path === routePath);
                                        return (
                                          <span
                                            key={routePath}
                                            className="px-2 py-1 bg-ac-01/20 text-ac-01 rounded-md text-xs flex items-center gap-1"
                                          >
                                            {route?.icon || "🔗"} {route?.label || routePath}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-xs text-fg-60 ml-6">No routes assigned to this role</p>
                                )}
                              </div>
                            ) : (
                              <div className="mt-2 flex items-center gap-2">
                                <Shield className="w-4 h-4 text-fg-60" />
                                <span className="text-sm text-fg-60">No role assigned (Full Access)</span>
                              </div>
                            )}
                          </div>
                          {user.role !== "super admin" && user.role !== "super_admin" && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditUserClick(user)}
                                className="flex items-center gap-2 px-3 py-1.5 bg-bg-50 text-fg-60 border border-bd-50 rounded-md hover:bg-bg-40 transition-colors"
                              >
                                <Edit2 className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user._id)}
                                className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 text-red-500 border border-red-500/20 rounded-md hover:bg-red-500/20 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
          </>
        )}

        {/* Create Role Modal */}
        {showCreateRoleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-bg-60 border border-bd-50 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-fg-50">Create New Role</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateForm({ name: "", description: "", allowedRoutes: [] });
                  }}
                  className="text-fg-60 hover:text-fg-50"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-fg-60 mb-1">
                    Role Name *
                  </label>
                  <input
                    type="text"
                    value={createRoleForm.name}
                    onChange={(e) => setCreateRoleForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-bd-50 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-01"
                    placeholder="e.g., Finance, Marketing, Support"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-fg-60 mb-1">
                    Description
                  </label>
                  <textarea
                    value={createRoleForm.description}
                    onChange={(e) => setCreateRoleForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-bd-50 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-01"
                    placeholder="Role description..."
                    rows={2}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-fg-60 mb-2">
                    Allowed Routes
                  </label>
                  <div className="border border-bd-50 rounded-md p-3 bg-bg-50 max-h-64 overflow-y-auto">
                    <div className="space-y-2">
                      {/* Select All Checkbox */}
                      <label className="flex items-center gap-2 p-2 hover:bg-bg-60 rounded cursor-pointer border-b border-bd-50 mb-2">
                        <input
                          type="checkbox"
                          checked={createRoleForm.allowedRoutes.length === ALL_ADMIN_ROUTES.length}
                          onChange={() => toggleSelectAll(true)}
                          className="w-4 h-4 text-ac-01 rounded focus:ring-ac-01"
                        />
                        <span className="text-sm font-semibold text-fg-50">
                          Select All Routes
                        </span>
                      </label>
                      {ALL_ADMIN_ROUTES.map((route) => {
                        const isSelected = createRoleForm.allowedRoutes.includes(route.path);
                        return (
                          <label
                            key={route.path}
                            className="flex items-center gap-2 p-2 hover:bg-bg-60 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRoute(route.path, true)}
                              className="w-4 h-4 text-ac-01 rounded focus:ring-ac-01"
                            />
                            <span className="text-sm text-fg-50">
                              {route.icon} {route.label}
                            </span>
                            <span className="text-xs text-fg-60 ml-auto">
                              {route.path}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <p className="text-xs text-fg-60 mt-1">
                    Selected {createRoleForm.allowedRoutes.length} of {ALL_ADMIN_ROUTES.length} routes
                  </p>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <button
                    onClick={() => {
                      setShowCreateRoleModal(false);
                      setCreateRoleForm({ name: "", description: "", allowedRoutes: [] });
                    }}
                    className="px-4 py-2 bg-bg-50 text-fg-60 border border-bd-50 rounded-md hover:bg-bg-40 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateRole}
                    className="px-4 py-2 bg-ac-01 text-white rounded-md hover:bg-ac-02 transition-colors"
                  >
                    Create Role
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create User Modal */}
        {showCreateUserModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-bg-60 border border-bd-50 rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-fg-50">Create Admin User</h2>
                <button
                  onClick={() => {
                    setShowCreateUserModal(false);
                    setCreateUserForm({ name: "", email: "", password: "", adminRoleId: "" });
                  }}
                  className="text-fg-60 hover:text-fg-50"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-fg-60 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={createUserForm.name}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-bd-50 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-01"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-fg-60 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={createUserForm.email}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-bd-50 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-01"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-fg-60 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    value={createUserForm.password}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border border-bd-50 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-01"
                    placeholder="Minimum 6 characters"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-fg-60 mb-1">
                    Assign Role (Optional)
                  </label>
                  <select
                    value={createUserForm.adminRoleId}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, adminRoleId: e.target.value }))}
                    className="w-full px-3 py-2 border border-bd-50 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-01"
                  >
                    <option value="">No Role (Full Access)</option>
                    {roles.map((role) => (
                      <option key={role._id} value={role._id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-fg-60 mt-1">
                    Leave empty to grant full admin access, or select a role to restrict access
                  </p>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <button
                    onClick={() => {
                      setShowCreateUserModal(false);
                      setCreateUserForm({ name: "", email: "", password: "", adminRoleId: "" });
                    }}
                    className="px-4 py-2 bg-bg-50 text-fg-60 border border-bd-50 rounded-md hover:bg-bg-40 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateUser}
                    className="px-4 py-2 bg-ac-01 text-white rounded-md hover:bg-ac-02 transition-colors"
                  >
                    Create User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Role Modal */}
        {showDeleteRoleModal && roleToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-bg-60 rounded-xl shadow-lg p-6 w-full max-w-md relative">
              <button
                className="absolute top-2 right-2 text-fg-60 hover:text-fg-50 text-2xl"
                onClick={() => {
                  setShowDeleteRoleModal(false);
                  setRoleToDelete(null);
                }}
              >
                &times;
              </button>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-fg-100">Delete Role</h2>
              </div>

              <div className="mb-6">
                <p className="text-fg-80 mb-2">
                  Are you sure you want to delete the role <strong>"{roleToDelete.name}"</strong>?
                </p>
                <p className="text-fg-60 text-sm">
                  This action cannot be undone. All users assigned to this role will lose their role assignment.
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs rounded bg-bg-50 text-fg-60 font-semibold hover:bg-bg-40 disabled:opacity-50"
                  onClick={() => {
                    setShowDeleteRoleModal(false);
                    setRoleToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs rounded bg-red-600 text-white dark:text-black font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  onClick={confirmDeleteRole}
                >
                  Delete Role
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Modal */}
        {showDeleteUserModal && userToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-bg-60 rounded-xl shadow-lg p-6 w-full max-w-md relative">
              <button
                className="absolute top-2 right-2 text-fg-60 hover:text-fg-50 text-2xl"
                onClick={() => {
                  setShowDeleteUserModal(false);
                  setUserToDelete(null);
                }}
              >
                &times;
              </button>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-fg-100">Delete User</h2>
              </div>

              <div className="mb-6">
                <p className="text-fg-80 mb-2">
                  Are you sure you want to delete the user <strong>"{userToDelete.name}"</strong>?
                </p>
                <p className="text-fg-60 text-sm">
                  This action cannot be undone. The user will lose access to the admin dashboard.
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs rounded bg-bg-50 text-fg-60 font-semibold hover:bg-bg-40 disabled:opacity-50"
                  onClick={() => {
                    setShowDeleteUserModal(false);
                    setUserToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs rounded bg-red-600 text-white dark:text-black font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  onClick={confirmDeleteUser}
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManagement;

